import React, { useEffect, useState } from "react";
import CustomModal from "../../components/common/Modal";
import DailyForm from "./RecurrencePatternsForms/DailyForm";
import WeeklyForm from "./RecurrencePatternsForms/WeeklyForm";
import MonthlyForm from "./RecurrencePatternsForms/MonthlyForm";
import YearlyForm from "./RecurrencePatternsForms/YearlyForm";
import { MONTHS, ORDINALS, WEEKDAYS, labelOf } from "./RecurrencePatternsForms/options";
import "./CustomRecurrenceModal.css";

/**
 * The Custom dialog behind Repeat → Custom on the create / edit page.
 *
 * The shared CustomModal is the container, at its small size. The four
 * patterns are the panels in RecurrencePatternsForms — Daily, Week, Month,
 * Year — and "Select:" swaps between them. Time is common to all four and
 * lives here.
 *
 * Range of Recurrence used to be here too. It is a field on the page now,
 * under Repeat, because when a schedule ends is worth seeing without opening a
 * dialog.
 *
 * The whole thing is edited on a draft copy, so Cancel really does discard:
 * nothing reaches the page until Save.
 */

const HOURS = Array.from({ length: 12 }, (_unused, i) => String(i + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_unused, i) => String(i * 5).padStart(2, "0"));

const MODES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Week" },
  { value: "monthly", label: "Month" },
  { value: "yearly", label: "Year" },
];

/* Every panel's state exists from the start, so switching Select: back and
   forth does not lose what was already filled in. */
export const defaultRecurrence = () => ({
  mode: "daily",
  time: { hour: "01", minute: "00", meridiem: "AM" },
  daily: { type: "every", interval: 1 },
  weekly: { interval: 1, days: ["mon"] },
  monthly: { type: "day", day: 1, interval: 1, ordinal: "first", weekday: "sun", theInterval: 1 },
  yearly: { type: "on", month: "jan", day: 1, ordinal: "first", weekday: "sun", theMonth: "jan" },
});

/* One line describing the rule, shown in the Repeat field once the dialog has
   been saved — otherwise the page would just say "Custom" and give no hint of
   what was set. When it ends is not part of this: that is the page's own Range
   of Recurrence field, already on screen next to it. */
export function describeRecurrence(rec) {
  if (!rec) return "Custom";
  const time = `${rec.time.hour}:${rec.time.minute} ${rec.time.meridiem}`;
  let base = "Custom";

  if (rec.mode === "daily") {
    base = rec.daily.type === "weekday" ? "Every weekday" : `Every ${rec.daily.interval} day(s)`;
  } else if (rec.mode === "weekly") {
    const days = rec.weekly.days.map((d) => labelOf(WEEKDAYS, d).slice(0, 3)).join(", ");
    base = `Every ${rec.weekly.interval} week(s)${days ? ` on ${days}` : ""}`;
  } else if (rec.mode === "monthly") {
    base =
      rec.monthly.type === "day"
        ? `Day ${rec.monthly.day} of every ${rec.monthly.interval} month(s)`
        : `The ${labelOf(ORDINALS, rec.monthly.ordinal)} ${labelOf(WEEKDAYS, rec.monthly.weekday)} of every ${rec.monthly.theInterval} month(s)`;
  } else if (rec.mode === "yearly") {
    base =
      rec.yearly.type === "on"
        ? `On ${labelOf(MONTHS, rec.yearly.month)} ${rec.yearly.day}`
        : `On the ${labelOf(ORDINALS, rec.yearly.ordinal)} ${labelOf(WEEKDAYS, rec.yearly.weekday)} of ${labelOf(MONTHS, rec.yearly.theMonth)}`;
  }

  return `${base} at ${time}`;
}

export default function CustomRecurrenceModal({ open, value, onCancel, onSave }) {
  const [draft, setDraft] = useState(value || defaultRecurrence());

  // Re-seed each time it opens, so a cancelled edit does not linger.
  useEffect(() => {
    if (open) setDraft(value || defaultRecurrence());
  }, [open, value]);

  const setPart = (key, part) => setDraft((d) => ({ ...d, [key]: part }));
  const setTime = (patch) => setDraft((d) => ({ ...d, time: { ...d.time, ...patch } }));

  const panel = {
    daily: <DailyForm value={draft.daily} onChange={(v) => setPart("daily", v)} />,
    weekly: <WeeklyForm value={draft.weekly} onChange={(v) => setPart("weekly", v)} />,
    monthly: <MonthlyForm value={draft.monthly} onChange={(v) => setPart("monthly", v)} />,
    yearly: <YearlyForm value={draft.yearly} onChange={(v) => setPart("yearly", v)} />,
  }[draft.mode];

  return (
    <CustomModal
      className="crm-modal"
      title="Custom"
      open={open}
      onClose={onCancel}
      size="small"
      footer={
        <div className="crm-footer">
          <button type="button" className="crm-btn crm-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="crm-btn crm-btn-primary" onClick={() => onSave(draft)}>
            Save
          </button>
        </div>
      }
    >
      <div className="crm-row crm-row-top">
        <span className="crm-field-label">Select:</span>
        <select
          className="crm-select crm-select-wide"
          value={draft.mode}
          onChange={(e) => setDraft((d) => ({ ...d, mode: e.target.value }))}
        >
          {MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      <div className="crm-row">
        <span className="crm-field-label">Time:</span>
        <select className="crm-select" value={draft.time.hour} onChange={(e) => setTime({ hour: e.target.value })}>
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span className="crm-colon">:</span>
        <select className="crm-select" value={draft.time.minute} onChange={(e) => setTime({ minute: e.target.value })}>
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          className="crm-select"
          value={draft.time.meridiem}
          onChange={(e) => setTime({ meridiem: e.target.value })}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>

      {panel}
    </CustomModal>
  );
}
